// api-base.service.ts
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable, throwError, from, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take, mergeMap, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { ApiDenial, ApiDenialService } from './api-denial.service';

const API_DENIAL_CODES: ReadonlyArray<ApiDenial['code']> = [
  'forbidden_tier', 'prod_writes_disabled', 'feature_disabled', 'not_in_ecase'
];

@Injectable({
  providedIn: 'root'
})
export class BaseApiService {
  protected apiUrl = environment.backend.uri;
  private readonly REFRESH_FAILED = Symbol('refresh_failed');
  private refreshTokenSubject: BehaviorSubject<string | symbol | null> = new BehaviorSubject<string | symbol | null>(null);
  private isRefreshing = false;
  // inject() rather than a constructor parameter: the subclasses call super(http, msalService).
  private readonly apiDenial = inject(ApiDenialService);

  constructor(private http: HttpClient, private msalService: MsalService) { }

  private getAuthHeaders(): Observable<HttpHeaders> {
    return this.getAccessToken().pipe(
      map((token: string) => new HttpHeaders({
        Authorization: `Bearer ${token}`
      }))
    );
  }

  protected get<T>(url: string): Observable<T> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<T>(url, { headers })),
      catchError(error => this.handleError(error, () => this.get<T>(url)))
    );
  }

  protected post<T>(url: string, body: any): Observable<T> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<T>(url, body, { headers })),
      catchError(error => this.handleError(error, () => this.post<T>(url, body)))
    );
  }

  protected put<T>(url: string, body: any): Observable<T> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.put<T>(url, body, { headers })),
      catchError(error => this.handleError(error, () => this.put<T>(url, body)))
    );
  }

  protected delete<T>(url: string): Observable<T> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.delete<T>(url, { headers })),
      catchError(error => this.handleError(error, () => this.delete<T>(url)))
    );
  }

  protected getBlob(url: string): Observable<Blob> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get(url, { headers, responseType: 'blob' })),
      catchError(error => this.handleError(error, () => this.getBlob(url)))
    );
  }  

  private handleError(error: HttpErrorResponse, retryCall: () => Observable<any>): Observable<any> {
    if (error.status === 401) {
      return this.handle401Error().pipe(
        mergeMap(() => retryCall())
      );
    }
    if (error.status === 403 && API_DENIAL_CODES.includes(error.error?.code)) {
      this.apiDenial.emit({ code: error.error.code, detail: error.error.detail });
    }
    return throwError(() => error);
  }

  private handle401Error(): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.refreshToken().pipe(
        switchMap((token: string) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token);
          return from(Promise.resolve(token));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          // Unblock any requests waiting on token refresh and fail them cleanly.
          this.refreshTokenSubject.next(this.REFRESH_FAILED);
          this.msalService.logoutRedirect();
          return throwError(() => new Error('Token refresh failed'));
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        mergeMap(token => {
          if (token === this.REFRESH_FAILED) {
            return throwError(() => new Error('Token refresh failed'));
          }
          return from(Promise.resolve(token));
        })
      );
    }
  }

  private refreshToken(): Observable<string> {
    return from(this.msalService.acquireTokenSilent({
      scopes: environment.backend.scopes,
      account: this.msalService.instance.getActiveAccount()!
    })).pipe(
      map((result: AuthenticationResult) => result.accessToken),
      catchError((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          return from(this.msalService.acquireTokenPopup({
            scopes: environment.backend.scopes
          })).pipe(
            map((result: AuthenticationResult) => result.accessToken)
          );
        }
        return throwError(() => error);
      })
    );
  }

  private getAccessToken(): Observable<string> {
    const account = this.msalService.instance.getActiveAccount();
    if (account) {
      return from(this.msalService.acquireTokenSilent({
        scopes: environment.backend.scopes,
        account: account
      })).pipe(
        map((result: AuthenticationResult) => result.accessToken),
        catchError(() => this.refreshToken())
      );
    }
    return throwError(() => new Error('No active account'));
  }

  public handleAuthError(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.handle401Error().pipe(
      mergeMap((token: string) => {
        const authReq = request.clone({
          setHeaders: { Authorization: `Bearer ${token}` }
        });
        return next.handle(authReq);
      })
    );
  }
}
